/**
 * The TAP Console groups (Kyu, 30 Sept): Sales/CG, Admin, Others.
 *
 * Stored on tasks.console_group and returned by read_huddle as each task's
 * 'group' key. The one rule lives here: null, a missing key (an older
 * read_huddle) or any value this build does not know reads as Others, so an
 * untagged task is never missing from Alex's tabs.
 */

export type ConsoleGroup = 'sales_cg' | 'admin' | 'others';

export const CONSOLE_GROUP_ORDER: readonly ConsoleGroup[] = ['sales_cg', 'admin', 'others'];

export const CONSOLE_GROUP_LABELS: Record<ConsoleGroup, string> = {
  sales_cg: 'Sales/CG',
  admin: 'Admin',
  others: 'Others',
};

export function consoleGroupOf(value: unknown): ConsoleGroup {
  return value === 'sales_cg' || value === 'admin' ? value : 'others';
}

/**
 * The trip tab (Kyu, 12 Oct; renamed LasVegas 2 Nov) and Parked (Kyu,
 * 2 Nov). No database value for either: a task whose title starts with
 * "Trip:" belongs to the trip tab, one that starts with "Parked:" to Parked
 * (any case, spaces allowed around the colon), and the tab shows the title
 * without the prefix. A bare "Trip:" or "Parked:" with nothing after it does
 * not count. Parked is a title prefix, not a console_group value, because
 * tasks' shape is pinned by the journey store: a new check value there
 * stopped every write on 2 Nov.
 */
export type ConsoleViewGroup = ConsoleGroup | 'trip' | 'parked';

export const TRIP_LABEL = 'LasVegas';
export const PARKED_LABEL = 'Parked';

const TRIP_PREFIX = /^\s*trip\s*:\s*(\S[\s\S]*)$/i;
const PARKED_PREFIX = /^\s*parked\s*:\s*(\S[\s\S]*)$/i;

/** The title without "Trip:", or null when the task is not a trip task. */
export function tripTitle(title: string): string | null {
  const match = TRIP_PREFIX.exec(title);
  return match === null ? null : match[1]!.trim();
}

/** The title without "Parked:", or null when the task is not parked. */
export function parkedTitle(title: string): string | null {
  const match = PARKED_PREFIX.exec(title);
  return match === null ? null : match[1]!.trim();
}

/** The title as shown: without its "Parked:" or "Trip:" prefix. */
export function consoleShownTitle(title: string): string {
  return parkedTitle(title) ?? tripTitle(title) ?? title;
}

/** The one tab a task shows in: Parked first, then the trip, otherwise its group. */
export function consoleViewGroupOf(task: { title: string; group?: unknown }): ConsoleViewGroup {
  if (parkedTitle(task.title) !== null) return 'parked';
  return tripTitle(task.title) === null ? consoleGroupOf(task.group) : 'trip';
}

/** The title as it is saved for the trip tab: "Trip: " in front, once. */
export function withTripPrefix(title: string): string {
  const trimmed = title.trim();
  return tripTitle(trimmed) === null ? `Trip: ${trimmed}` : trimmed;
}

/** The title as it is saved for Parked: "Parked: " in front, once. */
export function withParkedPrefix(title: string): string {
  const trimmed = title.trim();
  return parkedTitle(trimmed) === null ? `Parked: ${trimmed}` : trimmed;
}

/**
 * What to write when someone picks a tab in a row's select (Kyu, 13 Oct).
 * The title prefix stays the one truth: the trip and Parked add theirs
 * (console_group is left as it was); a group removes any prefix and sets that
 * group. Null when the pick changes nothing.
 */
export function tripPlacement(
  task: { title: string; console_group: string | null },
  choice: ConsoleViewGroup,
): { title?: string; console_group?: ConsoleGroup } | null {
  const bare = consoleShownTitle(task.title);
  const current = consoleViewGroupOf({ title: task.title, group: task.console_group });
  if (choice === current && (choice === 'trip' || choice === 'parked')) return null;
  if (choice === 'trip') return { title: withTripPrefix(bare) };
  if (choice === 'parked') return { title: withParkedPrefix(bare) };
  if (current === 'trip' || current === 'parked') return { title: bare, console_group: choice };
  return task.console_group === choice ? null : { console_group: choice };
}
